import type { ReactNode } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import type { PageProps } from '@inertiajs/core'
import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react'
import type { SetupOptions } from '@inertiajs/react/types/createInertiaApp'

export type AppSetup = SetupOptions<HTMLElement, PageProps>

export type InertiaMount = { unmount: () => void }

type Options = {
  id?: string
  title: (title: string) => string
  resolve: (name: string) => ResolvedComponent
  render: (App: AppSetup['App'], props: AppSetup['props']) => ReactNode
  subscribe?: () => () => void
}

// The entry is loaded on every page, but only Inertia-rendered actions emit the
// page-data script. Inertia v3 throws when it is absent, so plain Rails pages
// skip mounting. The container flag guards against a second evaluation of the
// entry: a second createRoot() clears the container but leaves the first tree
// mounted, stranding its <body> portals as inert duplicates. A module flag
// would reset per instance, so the DOM node carries it.
export async function mountInertiaApp({
  id = 'app',
  title,
  resolve,
  render,
  subscribe,
}: Options): Promise<InertiaMount | undefined> {
  if (!document.querySelector(`script[data-page="${id}"][type="application/json"]`)) return

  const container = document.getElementById(id)
  if (!container || container.dataset.inertiaMounted) return
  container.dataset.inertiaMounted = 'true'

  const unsubscribe = subscribe?.()
  let root: Root | undefined

  await createInertiaApp({
    id,
    title,
    resolve,
    progress: { color: '#7c3aed' },
    setup({ el, App, props }) {
      root = createRoot(el)
      root.render(render(App, props))
    },
  })

  return {
    unmount() {
      unsubscribe?.()
      root?.unmount()
      delete container.dataset.inertiaMounted
    },
  }
}
